import React, { useState, useEffect } from 'react';
import './SidebarMenu.css';
import { FaChevronRight, FaChevronDown, FaBars } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';

interface SubItem {
  label: string;
  path: string;
}

interface MenuItem {
  label: string;
  path?: string;
  subItems?: SubItem[];
}

const SidebarMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const location = useLocation();

  const toggleSidebar = (): void => setIsOpen(prev => !prev);
  const toggleSubmenu = (label: string): void => setExpanded(prev => (prev === label ? null : label));

  // Close sidebar or submenu on route change
  useEffect(() => {
    setIsOpen(false);
    setExpanded(null);
  }, [location.pathname]);

  // Define menu with subItems as objects including path
  const menuItems: MenuItem[] = [
    {
      label: 'Performance',
      subItems: [
        { label: 'P&L', path: '/performance/p&l' },
        { label: 'Scorecard', path: 'performance/scorecard' }
      ]
    },
    {
      label: 'Positions',
      subItems: [
        { label: 'Positions', path: '/positions' }
      ]
    },
    { label: 'Risk Dashboard', path: '/risk-dashboard' },
    {
      label: 'Market Risk',
      subItems: [
        { label: 'Dashboard', path: '/market-risk' },
        { label: 'Backtesting', path: '/market-risk/var' },
      ]
    },
    {
      label: 'Credit Risk',
      subItems: [
        { label: 'Dashboard', path: '/credit-risk/dashboard' }
      ]
    },
    {
      label: 'Funding Risk',
      subItems: [
        { label: 'Dashboard', path: '/funding-risk' }
      ]
    },
    {
      label: 'Compliance',
      subItems: [
        { label: 'FCA K-Factors', path: '/compliance/kfactors' }
      ]
    },
    {
      label: 'Pre-trade',
      subItems: [
        { label: 'VAR', path: '/pre-trade/var' },
        { label: 'K-Factors', path: '/pre-trade/k-factors' }
      ]
    },
    {
      label: 'Glossary',path: '/glossary',
    }
  ];

  return (
    <>
      <div className="menu-toggle" onClick={toggleSidebar}>
        <FaBars />
      </div>

      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          {menuItems.map((item, idx) => {
            const hasSub = Array.isArray(item.subItems);
            const isExpanded = expanded === item.label;

            return (
              <div key={idx} className="menu-item">
                {/* Top-level with direct path */}
                {item.path ? (
                  <Link to={item.path} className="menu-label">
                    {item.label}
                    <FaChevronRight className="arrow-icon static" />
                  </Link>
                ) : (
                  /* Expandable header */
                  <div
                    className="menu-label"
                    onClick={() => hasSub && toggleSubmenu(item.label)}
                  >
                    {item.label}
                    {hasSub ? (
                      isExpanded ? (
                        <FaChevronDown className="arrow-icon" />
                      ) : (
                        <FaChevronRight className="arrow-icon" />
                      )
                    ) : (
                      <FaChevronRight className="arrow-icon static" />
                    )}
                  </div>
                )}

                {/* Submenu items now with Links */}
                {hasSub && isExpanded && (
                  <div className="submenu expanded">
                    {item.subItems!.map((sub, subIdx) => (
                      <Link
                        key={subIdx}
                        to={sub.path}
                        className={`submenu-item ${location.pathname === sub.path ? 'active' : ''}`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SidebarMenu;
